import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  ArrowUpRight, ArrowRight, Cloud, GitMerge, LifeBuoy, Activity,
  ShieldCheck, Boxes, Cpu, Menu, X, Check, MoveRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Logo ---------------- */
const Logo = ({ className = "h-8 w-8" }) => (
  <svg viewBox="0 0 48 48" className={className} fill="none" data-testid="vecspec-logo">
    <defs>
      <linearGradient id="vg" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00F0FF" />
        <stop offset="1" stopColor="#00FF9D" />
      </linearGradient>
    </defs>
    <path d="M6 8 L24 40 L42 8" stroke="url(#vg)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 8 L24 24 L33 8" stroke="url(#vg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
  </svg>
);

/* ---------------- Particle Field ---------------- */
const ParticleField = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf, w, h, pts = [];
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      const n = Math.min(70, Math.floor((w * h) / 22000));
      pts = Array.from({ length: n }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35, vy: (Math.random() - 0.5) * 0.35,
      }));
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        for (let j = i + 1; j < pts.length; j++) {
          const q = pts[j];
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < 130) {
            ctx.strokeStyle = `rgba(0,240,255,${(1 - d / 130) * 0.16})`;
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.stroke();
          }
        }
        ctx.fillStyle = "rgba(0,255,157,0.7)";
        ctx.beginPath(); ctx.arc(p.x, p.y, 1.4, 0, Math.PI * 2); ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 h-full w-full" />;
};

/* ---------------- Reveal helper ---------------- */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const MonoLabel = ({ children, testid }) => (
  <div data-testid={testid} className="mono text-xs uppercase tracking-[0.28em] text-[color:var(--cyan)] font-semibold">
    {children}
  </div>
);

/* ---------------- Data ---------------- */
const STATS = [
  { v: "100+", l: "Mission-critical apps migrated" },
  { v: "~60", l: "Enterprise server clusters moved to AWS" },
  { v: "$2.4B", l: "Transaction volume governed" },
  { v: "Zero", l: "Downtime cutover commitment" },
];

const SERVICES = [
  { icon: Cloud, n: "01", t: "Cloud & Application Migration", d: "Legacy re-platforming, dependency mapping and zero-downtime cutover across AWS and multi-cloud. Proven at 100+ application scale." },
  { icon: GitMerge, n: "02", t: "Technology Program Delivery", d: "End-to-end governance, DevOps, CI/CD and FinOps for complex bank transformation programs — with boardroom-grade visibility." },
  { icon: LifeBuoy, n: "03", t: "Program Recovery & Assurance", d: "Rescuing off-track core modernizations. Vendor, schedule, budget and dependency triage with an executive recovery roadmap." },
];

const PRODUCTS = [
  {
    tag: "PRODUCT 01", t: "Technology Transformation Health Check", dur: "2–3 weeks",
    d: "A rapid executive assessment for programs that are behind schedule, over budget, or struggling with vendors and dependencies.",
    points: ["People → Process → Technology → Vendors", "Schedule, budget, risk & dependency triage", "Executive assessment + recovery roadmap", "90-day action plan"],
  },
  {
    tag: "PRODUCT 02", t: "Application & Cloud Migration Readiness", dur: "2–4 weeks",
    d: "Where deep hands-on migration experience becomes the proof point — grounded in 100+ apps / ~60 servers moved to AWS.",
    points: ["Application inventory & dependency mapping", "Migration readiness & sequencing", "Risk assessment & cutover considerations", "Governance model + implementation roadmap"],
  },
];

const MANIFESTO = [
  { n: "01", t: "Architecture First", d: "We understand the estate before touching it. Inventory, dependencies and blast-radius mapped before a single workload moves." },
  { n: "02", t: "No Tape Over Cracks", d: "Recovery means fixing root cause — vendor, process and governance — not papering over a slipping plan with more status decks." },
  { n: "03", t: "Boardroom Assurance", d: "Executives get a single, honest source of truth. Delivery confidence measured in evidence, not optimism." },
  { n: "04", t: "Startup Speed × Enterprise Rigor", d: "We move with the pace of a founding team and the discipline a tier-1 bank audit demands." },
];

const CAPS = ["Core Banking Modernization", "Cloud Engineering", "DevOps Automation", "Financial AI & Analytics", "Enterprise Application Dev", "IT Consulting & Staffing", "Product Engineering", "Startup Collaboration"];

const MARQUEE = ["FINANCIAL SERVICES", "INSURANCE MODERNIZATION", "HEALTHCARE PLATFORMS", "CLOUD MIGRATION", "PROGRAM RECOVERY", "DEVOPS AUTOMATION", "AI INTEGRATION", "ZERO-DOWNTIME CUTOVER"];

const INDUSTRIES = [
  { t: "Banking & Finance", d: "Core modernization, payments, and regulated cloud migration for banks and financial institutions." },
  { t: "Insurance", d: "Policy, claims and underwriting platform transformation with legacy-to-cloud re-platforming." },
  { t: "Healthcare & Life Sciences", d: "Secure, compliant systems for providers and payers — data, interoperability and cloud." },
  { t: "Public Sector & Utilities", d: "Mission-critical service modernization with strict governance and audit trails." },
  { t: "Retail & E-commerce", d: "Scalable, cloud-native commerce platforms and DevOps automation at peak load." },
  { t: "Technology & SaaS", d: "Product engineering and platform delivery for high-growth and enterprise software teams." },
];

const ENGAGEMENTS = ["Transformation Health Check", "Migration Readiness Assessment", "Program Recovery (SOS)", "General Consulting / Startup"];

/* ---------------- Nav ---------------- */
const Nav = () => {
  const [open, setOpen] = useState(false);
  const links = [["Services", "services"], ["Industries", "industries"], ["Products", "products"], ["Approach", "approach"], ["Contact", "contact"]];
  const go = (id) => { setOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#08090D]/80 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button onClick={() => go("top")} data-testid="nav-logo-btn" className="flex items-center gap-2.5 hoverable">
          <Logo className="h-7 w-7" />
          <span className="font-display font-bold text-lg tracking-tight">Vec<span className="text-cyan">Spec</span></span>
        </button>
        <nav className="hidden md:flex items-center gap-8">
          {links.map(([l, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)} className="text-sm text-[color:var(--txt2)] hover:text-white hoverable">{l}</button>
          ))}
          <button data-testid="nav-cta" onClick={() => go("contact")} className="text-sm font-semibold px-4 py-2 rounded-full bg-[color:var(--cyan)] text-[#001014] hover:brightness-110 hoverable">Request Health Check</button>
        </nav>
        <button className="md:hidden" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#08090D] px-5 py-4 flex flex-col gap-4">
          {links.map(([l, id]) => (
            <button key={id} data-testid={`nav-m-${id}`} onClick={() => go(id)} className="text-left text-[color:var(--txt2)] hover:text-white">{l}</button>
          ))}
          <button data-testid="nav-m-cta" onClick={() => go("contact")} className="text-sm font-semibold px-4 py-2.5 rounded-full bg-[color:var(--cyan)] text-[#001014]">Request Health Check</button>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const HERO_LINES = ["Difficult technology", "transformations,", "delivered."];
const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden grid-lines">
      <motion.div style={{ y }} className="absolute inset-0">
        <ParticleField />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#08090D]/40 via-[#08090D]/70 to-[#08090D]" />
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full blur-[140px] opacity-20 bg-[color:var(--cyan)]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 mb-8" data-testid="hero-badge">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--emerald)] opacity-70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--emerald)]" /></span>
          <span className="mono text-xs uppercase tracking-[0.22em] text-[color:var(--txt2)]">Enterprise Tech Transformation &amp; Delivery</span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.03] max-w-5xl">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
                {i === 2 ? <>delivered<span className="text-cyan">.</span></> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-[color:var(--txt2)] leading-relaxed">
          VecSpec is the specialist firm banks, insurers, healthcare and financial institutions call for complex technology programs — application &amp; cloud migration, delivery governance, and recovery of at-risk transformations. Across Finance, Insurance, Healthcare, Cloud, DevOps and AI.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }} className="mt-10 flex flex-wrap gap-4">
          <button data-testid="hero-cta-primary" onClick={() => go("contact")} className="group inline-flex items-center gap-2 rounded-full bg-[color:var(--cyan)] text-[#001014] font-semibold px-6 py-3.5 hover:brightness-110 hoverable glow-cyan">
            Request a Health Check <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <button data-testid="hero-cta-secondary" onClick={() => go("services")} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-white hover:bg-white/5 hoverable">
            Explore Transformations <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Marquee ---------------- */
const Marquee = () => (
  <div className="border-y border-white/10 bg-[#0B0C12] py-5 overflow-hidden">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <div key={k} className="flex items-center">
          {MARQUEE.map((m, i) => (
            <span key={i} className="mx-8 font-display text-xl sm:text-2xl font-semibold text-white/40 flex items-center gap-8">
              {m} <span className="text-[color:var(--cyan)]">◆</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ---------------- Stats ---------------- */
const Stats = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
      {STATS.map((s, i) => (
        <Reveal key={i} delay={i * 0.08} className="bg-[#0B0C12] p-7 sm:p-9">
          <div className="font-display text-4xl sm:text-5xl font-bold text-white">{s.v}</div>
          <div className="mt-3 text-sm text-[color:var(--txt2)] leading-relaxed">{s.l}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Services ---------------- */
const Services = () => (
  <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
    <Reveal><MonoLabel testid="services-label">/ Core Services</MonoLabel></Reveal>
    <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight max-w-3xl">Three ways we move critical technology forward.</h2></Reveal>
    <div className="mt-14 grid md:grid-cols-3 gap-5">
      {SERVICES.map((s, i) => (
        <Reveal key={i} delay={i * 0.1}>
          <div data-testid={`service-card-${i}`} className="group h-full rounded-2xl border border-white/10 bg-[#0F1117] p-7 hover:border-[color:var(--cyan)]/40 hoverable">
            <div className="flex items-center justify-between">
              <div className="h-12 w-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[color:var(--cyan)]/10 hoverable">
                <s.icon className="h-6 w-6 text-[color:var(--cyan)]" />
              </div>
              <span className="mono text-xs text-white/30">{s.n}</span>
            </div>
            <h3 className="mt-6 font-display text-xl font-medium">{s.t}</h3>
            <p className="mt-3 text-sm text-[color:var(--txt2)] leading-relaxed">{s.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Products ---------------- */
const Products = () => {
  const go = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="products" className="border-y border-white/10 bg-[#0A0B10]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal><MonoLabel testid="products-label">/ Signature Products</MonoLabel></Reveal>
        <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight max-w-3xl">Fixed-scope assessments that are easy to say yes to.</h2></Reveal>
        <div className="mt-14 grid lg:grid-cols-2 gap-5">
          {PRODUCTS.map((p, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`product-card-${i}`} className="h-full rounded-2xl border border-white/10 bg-[#0F1117] p-8 flex flex-col">
                <div className="flex items-center justify-between">
                  <MonoLabel>{p.tag}</MonoLabel>
                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 mono text-xs text-[color:var(--emerald)]">{p.dur}</span>
                </div>
                <h3 className="mt-5 font-display text-2xl font-medium">{p.t}</h3>
                <p className="mt-3 text-sm text-[color:var(--txt2)] leading-relaxed">{p.d}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-white/80">
                      <Check className="h-4 w-4 mt-0.5 text-[color:var(--cyan)] shrink-0" /> {pt}
                    </li>
                  ))}
                </ul>
                <button data-testid={`product-cta-${i}`} onClick={go} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[color:var(--cyan)] hoverable">
                  Start this engagement <MoveRight className="h-4 w-4" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Industries ---------------- */
const Industries = () => (
  <section id="industries" className="border-y border-white/10 bg-[#0A0B10]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <Reveal><MonoLabel testid="industries-label">/ Industries</MonoLabel></Reveal>
      <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight max-w-3xl">We deliver across every regulated, mission-critical sector.</h2></Reveal>
      <Reveal delay={0.1}><p className="mt-5 text-[color:var(--txt2)] max-w-2xl leading-relaxed">From finance and insurance to healthcare and the public sector — wherever technology programs are complex, regulated and cannot fail, VecSpec brings the delivery leadership to see them through.</p></Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {INDUSTRIES.map((s, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08}>
            <div data-testid={`industry-card-${i}`} className="group h-full rounded-2xl border border-white/10 bg-[#0F1117] p-7 hover:border-[color:var(--emerald)]/40 hoverable">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-medium group-hover:translate-x-1 transition-transform">{s.t}</h3>
                <ArrowUpRight className="h-5 w-5 text-white/25 group-hover:text-[color:var(--emerald)] transition-colors" />
              </div>
              <p className="mt-3 text-sm text-[color:var(--txt2)] leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Manifesto ---------------- */
const Manifesto = () => (
  <section id="approach" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
    <Reveal><MonoLabel testid="approach-label">/ The Approach</MonoLabel></Reveal>
    <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight max-w-3xl">A delivery philosophy built for programs that cannot fail.</h2></Reveal>
    <div className="mt-14 border-t border-white/10">
      {MANIFESTO.map((m, i) => (
        <Reveal key={i} delay={i * 0.06}>
          <div data-testid={`manifesto-${i}`} className="group grid md:grid-cols-12 gap-4 items-start py-8 border-b border-white/10 hover:bg-white/[0.02] hoverable px-2">
            <div className="md:col-span-2 mono text-[color:var(--cyan)] text-lg">{m.n}</div>
            <h3 className="md:col-span-4 font-display text-2xl font-medium group-hover:translate-x-1 transition-transform">{m.t}</h3>
            <p className="md:col-span-6 text-[color:var(--txt2)] leading-relaxed">{m.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Capabilities ---------------- */
const Capabilities = () => (
  <section id="capabilities" className="border-y border-white/10 bg-[#0A0B10]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <Reveal><MonoLabel testid="capabilities-label">/ Capabilities</MonoLabel></Reveal>
      <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight max-w-3xl">Full-spectrum, from core banking to AI.</h2></Reveal>
      <div className="mt-12 flex flex-wrap gap-3">
        {CAPS.map((c, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <span data-testid={`cap-${i}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0F1117] px-5 py-2.5 text-sm text-white/80 hover:border-[color:var(--cyan)]/50 hover:text-white hoverable">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--emerald)]" /> {c}
            </span>
          </Reveal>
        ))}
      </div>
      <div className="mt-14 grid sm:grid-cols-3 gap-5">
        {[[ShieldCheck, "Enterprise rigor", "Audit-ready governance for tier-1 institutions."], [Boxes, "Product engineering", "SaaS, cloud-native and B2B platforms shipped end-to-end."], [Cpu, "AI-enabled delivery", "Agentic analytics and automation woven into transformation."]].map(([Ic, t, d], i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="rounded-2xl border border-white/10 bg-[#0F1117] p-7">
              <Ic className="h-6 w-6 text-[color:var(--cyan)]" />
              <h3 className="mt-4 font-display text-lg font-medium">{t}</h3>
              <p className="mt-2 text-sm text-[color:var(--txt2)]">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", engagement: ENGAGEMENTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) { toast.error("Please fill name, email and company."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request received. We'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", role: "", engagement: ENGAGEMENTS[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div>
          <Reveal><MonoLabel testid="contact-label">/ Start a Conversation</MonoLabel></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight">Have a program that needs experienced delivery leadership?</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-5 text-[color:var(--txt2)] leading-relaxed max-w-md">Tell us where the program stands. We respond within one business day with a proposed scope for a Health Check or Migration Readiness assessment.</p></Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-3 text-sm">
              <div className="flex items-center gap-3 text-white/80"><Activity className="h-4 w-4 text-[color:var(--cyan)]" /> hello@vecspec.io</div>
              <div className="flex items-center gap-3 text-white/80"><ShieldCheck className="h-4 w-4 text-[color:var(--cyan)]" /> Confidential — NDA on request</div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-[#0F1117] p-7 sm:p-9 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" testid="field-name"><input data-testid="input-name" value={form.name} onChange={set("name")} className="inp" placeholder="Jane Doe" /></Field>
              <Field label="Work email" testid="field-email"><input data-testid="input-email" type="email" value={form.email} onChange={set("email")} className="inp" placeholder="jane@bank.com" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Company" testid="field-company"><input data-testid="input-company" value={form.company} onChange={set("company")} className="inp" placeholder="Acme Bank" /></Field>
              <Field label="Role" testid="field-role"><input data-testid="input-role" value={form.role} onChange={set("role")} className="inp" placeholder="Head of Delivery" /></Field>
            </div>
            <Field label="Engagement" testid="field-engagement">
              <select data-testid="input-engagement" value={form.engagement} onChange={set("engagement")} className="inp">
                {ENGAGEMENTS.map((o) => <option key={o} value={o} className="bg-[#0F1117]">{o}</option>)}
              </select>
            </Field>
            <Field label="What's happening with the program?" testid="field-message">
              <textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={4} className="inp resize-none" placeholder="Behind schedule, vendor issues, migration readiness…" />
            </Field>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--cyan)] text-[#001014] font-semibold px-6 py-3.5 hover:brightness-110 disabled:opacity-60 hoverable">
              {loading ? "Sending…" : <>Request a Health Check <ArrowUpRight className="h-4 w-4" /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, testid, children }) => (
  <label data-testid={testid} className="block">
    <span className="mono text-xs uppercase tracking-[0.18em] text-[color:var(--txt2)]">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

/* ---------------- Footer ---------------- */
const Footer = () => (
  <footer className="border-t border-white/10 bg-[#08090D]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-2.5">
          <Logo className="h-7 w-7" />
          <span className="font-display font-bold text-lg">Vec<span className="text-cyan">Spec</span></span>
        </div>
        <p className="mono text-xs uppercase tracking-[0.22em] text-[color:var(--txt2)] max-w-xs">Enterprise Technology Transformation &amp; Delivery</p>
      </div>
      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/40">
        <span>© {new Date().getFullYear()} Vector Spectrum. All rights reserved.</span>
        <span>Application Migration · Cloud · Program Recovery · AI</span>
      </div>
    </div>
  </footer>
);

/* ---------------- App ---------------- */
function App() {
  return (
    <div className="grain min-h-screen bg-[#08090D] text-white relative">
      <style>{`.inp{width:100%;background:#0A0B10;border:1px solid rgba(255,255,255,0.1);border-radius:0.75rem;padding:0.7rem 0.9rem;color:#fff;font-size:0.9rem;outline:none;transition:border-color .25s}.inp:focus{border-color:var(--cyan)}`}</style>
      <Toaster theme="dark" position="bottom-right" richColors />
      <Nav />
      <main className="relative z-10">
        <Hero />
        <Marquee />
        <Stats />
        <Services />
        <Industries />
        <Products />
        <Manifesto />
        <Capabilities />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
